import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { ClipboardList, Maximize2, PhoneOff } from 'lucide-react'
import type { CallPhase } from './callMachine'
import { isWithheldNumber } from './displayPhone'
import { CallTimer } from './scene/CallTimer'
import { SCENE_ROUTE, isSceneRoute } from './sceneRoute'
import { useCallSelector } from './selectors'
import { useTelephony } from './TelephonyProvider'

/**
 * BARRE D'APPEL GLOBALE — l'appel engagé suit l'agent sur TOUTE page.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * POURQUOI (issue #220, constat prod du 2026-10-12)
 * ══════════════════════════════════════════════════════════════════════════
 *
 * La scène (`/v2/softphone`) est le seul écran qui rende le panneau d'appel
 * et la qualification. Un agent qui décroche un entrant depuis une autre page
 * — ou qui quitte la scène en pleine conversation — se retrouvait avec une
 * ligne ouverte et AUCUNE commande : ni « Raccrocher », ni accès au wrapup.
 * Il rafraîchissait, la machine repartait à `idle`, et le serveur le comptait
 * encore 180 s « en wrapup » (entrants suivants mis en file puis MISSED).
 *
 * Rendue par le provider (comme `IncomingCallOverlay` et
 * `PendingOutcomeBanner`), donc présente partout. Vue PURE sur la machine :
 * aucun état local, aucun appel réseau — « Raccrocher » est la fonction du
 * provider, celle que la scène appelle.
 *
 * QUAND : dès qu'une ligne est engagée (`dialing`, `ringing`, `active`) ou
 * qu'un appel attend sa qualification (`wrapup`). Jamais en `incoming` (c'est
 * l'overlay qui parle) ni au repos. Et JAMAIS sur la scène elle-même, qui
 * rend déjà tout cela en grand.
 *
 * OÙ : fixe en bas, pleine largeur, `pb-safe` (barre d'accueil iPad). Dans
 * l'empilement : SOUS l'overlay d'entrant (`z-[100]` — un entrant pendant un
 * wrapup doit la recouvrir) et SOUS le bandeau de résultat non enregistré
 * (`z-[9998]`), au-dessus du contenu des pages. Rien n'est en `fixed` dans le
 * châssis à part son en-tête (haut, `z-[100]`) : pas de chevauchement.
 *
 * Cibles tactiles ≥ 44 px (iPad). Palette : celle du bandeau de résultat
 * (blanc / `dark:gray-800`), pour que `CallTimer` — dont les couleurs sont
 * fixées — reste lisible dans les deux thèmes.
 */

/** Phases où la barre a quelque chose à montrer. */
type ShownPhase = Extract<CallPhase, 'dialing' | 'ringing' | 'active' | 'wrapup'>
const SHOWN_PHASES: readonly ShownPhase[] = ['dialing', 'ringing', 'active', 'wrapup']

function isShownPhase(phase: CallPhase): phase is ShownPhase {
  return (SHOWN_PHASES as readonly CallPhase[]).includes(phase)
}

/** Libellé d'état par phase — clés du namespace `telephony`. */
const PHASE_LABEL_KEYS: Record<ShownPhase, string> = {
  dialing: 'call.connecting',
  ringing: 'call.ringing',
  active: 'activeCallBar.active',
  wrapup: 'activeCallBar.wrapup',
}

const BUTTON =
  'flex min-h-[44px] items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-colors'

/**
 * LA RÈGLE D'AFFICHAGE, en UN seul endroit : la phase à montrer (`null` si la
 * barre n'a rien à dire). Phase ∈ `SHOWN_PHASES` ET hors de la scène. La barre
 * la lit pour se rendre ; le châssis la lit (`useActiveCallBarShown`) pour
 * dégager le bas du `<main>` — mesuré en recette (D1) : à 1024×768 la barre
 * (61 px) recouvrait entièrement le dernier bouton d'une page longue. Les deux
 * ne peuvent pas diverger puisqu'ils lisent la même fonction.
 *
 * SÉLECTEUR, jamais l'état complet : lue sur toute page, elle ne doit pas se
 * recalculer à chaque seconde d'un rebours d'auto-dial.
 */
export function useActiveCallBarPhase(): ShownPhase | null {
  const phase = useCallSelector((state) => state.phase)
  const { pathname } = useLocation()
  if (!isShownPhase(phase)) return null
  if (isSceneRoute(pathname)) return null
  return phase
}

/** Vrai quand la barre est à l'écran — pour l'espaceur du châssis (`AppShell`). */
export function useActiveCallBarShown(): boolean {
  return useActiveCallBarPhase() !== null
}

export function ActiveCallBar() {
  const { t } = useTranslation('telephony')
  const shown = useActiveCallBarPhase()
  const contactName = useCallSelector((state) => state.contactName)
  const phoneNumber = useCallSelector((state) => state.phoneNumber)
  const telephony = useTelephony()
  const navigate = useNavigate()

  if (shown === null) return null

  const inWrapup = shown === 'wrapup'
  // Même règle que l'overlay (B3) : un appelant anonyme arrive avec un marqueur
  // Twilio en guise de numéro — l'agent lit « Numéro masqué ».
  const withheld = isWithheldNumber(phoneNumber)
  const primary =
    contactName ||
    (withheld ? t('call.withheldNumber') : phoneNumber) ||
    t('call.unknownNumber')
  const secondary = contactName && phoneNumber && !withheld ? phoneNumber : null

  return (
    <div
      role="region"
      aria-label={t('activeCallBar.label')}
      data-testid="active-call-bar"
      className="fixed inset-x-0 bottom-0 z-[90] border-t border-gray-200 bg-white pb-safe shadow-[0_-8px_24px_rgba(15,23,42,0.12)] dark:border-gray-700 dark:bg-gray-800"
    >
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-2">
        <span className="relative flex h-2 w-2 shrink-0" aria-hidden="true">
          <span
            className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${
              inWrapup ? 'bg-amber-500' : 'bg-blue-500'
            }`}
          />
          <span
            className={`relative inline-flex h-2 w-2 rounded-full ${
              inWrapup ? 'bg-amber-500' : 'bg-blue-500'
            }`}
          />
        </span>

        <div className="min-w-0 flex-1">
          <p
            className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${
              inWrapup ? 'text-amber-600 dark:text-amber-400' : 'text-blue-600 dark:text-blue-400'
            }`}
          >
            {t(PHASE_LABEL_KEYS[shown])}
          </p>
          <p className="flex items-baseline gap-2 text-sm font-semibold text-gray-900 dark:text-white">
            {/* Un numéro ne se coupe jamais chiffre par chiffre (REGISTRE §5) :
                c'est le NOM qui se tronque, le numéro reste entier. */}
            <span className="truncate">{primary}</span>
            {secondary && (
              <span className="shrink-0 whitespace-nowrap text-xs font-normal text-gray-500 dark:text-gray-400">
                {secondary}
              </span>
            )}
          </p>
        </div>

        {/* Le chronomètre ne court qu'en conversation : en wrapup la durée est
            figée et appartient au formulaire de qualification. */}
        {shown === 'active' && (
          <div className="hidden shrink-0 sm:block">
            <CallTimer />
          </div>
        )}

        {inWrapup ? (
          <button
            type="button"
            onClick={() => navigate(SCENE_ROUTE)}
            className={`${BUTTON} bg-amber-500 text-white hover:bg-amber-600`}
          >
            <ClipboardList className="h-4 w-4" aria-hidden="true" />
            {t('activeCallBar.qualify')}
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={() => telephony.hangup()}
              className={`${BUTTON} bg-red-600 text-white hover:bg-red-700`}
            >
              <PhoneOff className="h-4 w-4" aria-hidden="true" />
              {t('call.hangup')}
            </button>
            <button
              type="button"
              onClick={() => navigate(SCENE_ROUTE)}
              className={`${BUTTON} border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700`}
            >
              <Maximize2 className="h-4 w-4" aria-hidden="true" />
              {t('activeCallBar.open')}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
